import { validEmail } from 'core/utils';
import { UseRouter } from 'next/router';
import Track from 'src/authentication/handlers/track';
import { getReduxStore } from 'src/redux';
import Firestore from './firestore';
import { updateDemographicInformation, updateUserProfile } from './api';
import { UpdateDemographicInfoRequest } from '../models/api';

const firestore = Firestore.getInstance();

export const handleError = (e: { code: string | number; message: string }) => {
  let returnObj = {};
  if (!e || !e.code) {
    return e;
  }
  switch (e.code) {
    case 'auth/invalid-email':
      returnObj = {
        errorMessage: 'Please enter a valid email',
        errorFields: ['email'],
      };
      break;
    case 'auth/user-not-found':
      returnObj = {
        errorMessage: "This email doesn't exist. Try another.",
        errorFields: ['email'],
      };
      break;
    case 'auth/wrong-password':
      returnObj = {
        errorMessage: 'Invalid password',
        errorFields: ['password'],
      };
      break;
    case 'auth/account-exists-with-different-credential':
      returnObj = {
        errorMessage: 'This account was registered with a different provider. Try Google, Facebook or Email.',
        errorFields: [],
      };
      break;
    case 'auth/network-request-failed':
      returnObj = {
        errorMessage: 'Network error, please refresh and try again.',
        errorFields: [],
      };
      break;
    case 'auth/too-many-requests':
      returnObj = {
        errorMessage: 'Unusual activity detected, try again in 1-2 mins',
        errorFields: [],
      };
      break;
    case 'auth/facebook-email-null':
      returnObj = {
        errorMessage: 'Please choose another method, Email ID does not exist for select Facebook account',
        errorFields: [],
      };
      break;
    case 'auth/user-cancelled':
      returnObj = {
        errorMessage: 'Authentication cancelled by user',
        errorFields: [],
      };
      break;
    case 'auth/user-disabled':
      returnObj = {
        errorMessage: 'QuillBot has disabled your account; see the Help Center for more information.',
        errorFields: [],
      };
      break;
    case 'auth/popup-closed-by-user':
      returnObj = {
        errorMessage: 'The popup has been closed by the user before finalizing the operation.',
        errorFields: [],
      };
      break;
    // handle firebase issue when cache is cleaned
    // more details: https://github.com/firebase/firebase-js-sdk/issues/1926
    case 11:
      returnObj = {
        errorMessage: 'Something went wrong, Please refresh the page',
        errorFields: [],
      };
      break;
    case 'auth/email-already-in-use':
      returnObj = {
        errorMessage: 'The email address is already in use by another account.',
        errorFields: [],
      };
      break;
    // firebase issue handling ends here

    default:
      // handle case for word extension popup block error
      if (
        e.code === 'auth/popup-blocked' &&
        getReduxStore()?.getState()?.common.platform?.extensionMetaData?.type === 'ms-word'
      ) {
        returnObj = { errorMessage: 'wordPopupError', errorFields: [] };
      } else if (e.message) returnObj = { errorMessage: e.message, errorFields: [] };
      else returnObj = { errorMessage: 'An error happened', errorFields: [] };
      break;
  }
  return returnObj;
};

export const login = (
  provider: 'google' | 'facebook' | 'email',
  email?: string,
  password?: string,
  triggeredBy?: string,
  idToken?: string
) => {
  const data = {
    provider,
    email,
    password,
    triggeredBy,
    idToken,
  };
  return new Promise((resolve, reject) => {
    firestore.login(
      data,
      (res: { created: boolean; uid: string; fullName: string; email: string }) => {
        if (!res.created) {
          Track.authentication('loginSuccess', data.provider, triggeredBy);
        } else {
          Track.authentication('signupSuccess', data.provider, triggeredBy);
        }
        resolve(res);
      },
      (e: { code: string; message: string }) => {
        reject(handleError(e));
      }
    );
  });
};
export const signup = (username: string, password: string) =>
  new Promise((resolve, reject) => {
    firestore.createUser(
      username,
      password,
      (res: { uid: string; fullName: string; email: string }) => {
        Track.authentication('signupSuccess', 'email');
        resolve(res);
      },
      (e: { code: string; message: string }) => {
        let errorObj = {};
        if (!password) {
          if (e.code === 'auth/weak-password') {
            reject({ authState: 'userNotExist' });
          } else if (e.code === 'auth/email-already-in-use') {
            reject({ authState: 'userExist' });
          }
        } else {
          errorObj = handleError(e);
        }
        reject(errorObj);
      }
    );
  });
export const setName = (event: any) => {
  if (event && event.target && event.target.value) {
    firestore.setName(event.target.value);
  }
};
export const handleSubscriptionChange = (event: any) => {
  if (event && event.target) {
    firestore.setEmailSubscription(event.target.checked);
  }
};
export const checkEmailExist = (email: string) =>
  firestore
    .checkSignInMethod(email)
    .then((res: string | any[]) => {
      if (res && Array.isArray(res) && !res.length) {
        return { authState: 'userNotExist' };
      }
      if (res && res.length) {
        return { authState: 'userExist' };
      }
      return {};
    })
    .catch((error: { code: string | number; message: string }) => handleError(error));

export const validateEmail = (email: string) => {
  if (!email || !validEmail(email)) {
    return false;
  }
  return true;
};

export const addPersonalizeInfo = (obj: any) => {
  const demographicPatch = { ...obj, uid: firestore.getUserID() };
  updateDemographicInformation(demographicPatch as UpdateDemographicInfoRequest).then(() => {
    Firestore.getInstance().updateFirestoreUser(obj);
  });
};

export const setSkipPersonalization = (skipped: boolean) => {
  const uid = getReduxStore()?.getState()?.user?.uid;
  const userData = { uid, skippedPersonalization: skipped };
  if (userData.uid) updateUserProfile(userData);
};

export const handleAuthTrack = (router: UseRouter) => {
  const queryObj = { ...router.query };
  if (router.pathname !== '/upgrade') {
    Track.pageLoaded(router.pathname, queryObj.referrer as string);
  }
  if (queryObj.referrer) {
    delete queryObj.referrer;
    router.replace({
      pathname: router.pathname,
      query: queryObj,
    });
  }
  /* const newSearchParams = queryObj.toString();
  // eslint-disable-next-line prefer-template
  const newLocation = location.pathname + '?' + newSearchParams;
  Router.replace(newLocation); */
};

export const handlePremiumRedirect = (router: UseRouter) => {
  const queryObj = { ...router.query };
  if (queryObj.returnUrl === '/premium' || queryObj.returnUrl === '/upgrade') {
    const { returnUrl } = queryObj;
    delete queryObj.returnUrl;
    router.push({
      pathname: returnUrl,
      query: { ...queryObj, referrer: 'upgradeFlowRedirect' },
    });
  }
};

export const handleReturnUrl = (router: UseRouter) => {
  const queryObj = { ...router.query };
  let redirectUrl = '/';
  if (queryObj.returnUrl) {
    if (queryObj.returnUrl !== '/404') redirectUrl = <string>queryObj.returnUrl;
    delete queryObj.returnUrl;
  }

  router.push({ pathname: redirectUrl, query: queryObj });
};
